'use client';

/**
 * SCIENCES CORANIQUES — Ulūm al-Qurʾān.
 * Bannière dorée, 10 disciplines : filtres par domaine, cartes savantes
 * avec CONTENU COMPLET dépliable dans la page (exigence III) :
 * corps intégral + verset de référence (arabe + traduction).
 */
import { useMemo, useState } from 'react';
import { useApp } from '@/lib/store';
import { DICTS } from '@/lib/i18n';
import PageBanner from './PageBanner';
import { SCIENCES, SCIENCES_CATS } from '@/data/sciences';
import { cn } from '@/lib/utils';
import {
  Cloud, BookOpen, Crown, Volume2, ScrollText, History, Layers, Sparkles, Type,
  Landmark, Quote, ChevronDown,
} from 'lucide-react';

const ICONS: Record<string, React.ReactNode> = {
  cloud: <Cloud className="h-6 w-6" />, book: <BookOpen className="h-6 w-6" />,
  crown: <Crown className="h-6 w-6" />, volume: <Volume2 className="h-6 w-6" />,
  scroll: <ScrollText className="h-6 w-6" />, history: <History className="h-6 w-6" />,
  layers: <Layers className="h-6 w-6" />, sparkles: <Sparkles className="h-6 w-6" />,
  type: <Type className="h-6 w-6" />, landmark: <Landmark className="h-6 w-6" />,
};

export default function SciencesSection() {
  const { uiLang } = useApp();
  const t = DICTS[uiLang];
  const bl = uiLang === 'ar' ? 'fr' : uiLang; // corps/résumés : FR ou EN
  const [cat, setCat] = useState<string>('all');
  const [openId, setOpenId] = useState<string | null>(null);

  const list = useMemo(
    () => (cat === 'all' ? SCIENCES : SCIENCES.filter((a) => a.cat === cat)),
    [cat]
  );

  return (
    <section className="relative min-h-screen py-10" aria-label={t.sciences.title}>
      {/* Bannière dorée */}
      <PageBanner
        src="/images/banners/sciences_banner.png"
        eyebrow="ʿUlūm al-Qurʾān — علوم القرآن"
        title={t.sciences.title}
        sub={t.sciences.subtitle}
      />

      <div
        aria-hidden
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(ellipse 55% 40% at 50% 0%, rgba(212,175,55,0.12), transparent 65%), radial-gradient(ellipse 40% 30% at 12% 80%, rgba(46,139,110,0.10), transparent 70%)',
        }}
      />

      <div className="relative mx-auto max-w-6xl px-4 pt-10 sm:px-6">

        {/* Filtres */}
        <div className="mb-8 flex flex-wrap justify-center gap-2">
          {SCIENCES_CATS.map((c) => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={cn(
                'rounded-full border px-4 py-1.5 text-xs font-bold transition-all',
                cat === c
                  ? 'border-[#d4af37] bg-[#d4af37] text-[#032019]'
                  : 'border-[rgba(212,175,55,0.3)] text-[#cfe0cf]/75 hover:border-[rgba(212,175,55,0.6)] hover:text-[#f0d98c]'
              )}
            >
              {t.sciences.cats[c]}
              {c !== 'all' && (
                <span className="ml-1.5 opacity-70">({SCIENCES.filter((a) => a.cat === c).length})</span>
              )}
            </button>
          ))}
        </div>

        {/* Cartes — contenu complet dépliable dans la page */}
        <div className="grid gap-4 md:grid-cols-2">
          {list.map((a, i) => {
            const open = openId === a.id;
            return (
              <article
                key={a.id}
                className="glass-gold glass-gold-hover group relative overflow-hidden rounded-2xl"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <span
                  aria-hidden
                  className="pointer-events-none absolute -right-4 -top-8 font-serif-royal text-[100px] font-bold leading-none opacity-[0.06]"
                >
                  {String(i + 1).padStart(2, '0')}
                </span>
                <button
                  onClick={() => setOpenId(open ? null : a.id)}
                  aria-expanded={open}
                  className="flex w-full items-start gap-4 p-6 text-left"
                >
                  <span
                    className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border transition-transform duration-300 group-hover:scale-110"
                    style={{ borderColor: `${a.accent}55`, backgroundColor: `${a.accent}18`, color: a.accent }}
                  >
                    {ICONS[a.icon] ?? <BookOpen className="h-6 w-6" />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[15px] font-bold text-[#f5f0e1] group-hover:text-[#f0d98c]">
                      {a.title[uiLang]}
                    </span>
                    <span className="mt-0.5 block font-arabic text-lg leading-snug text-[#f0d98c]/80" dir="rtl" lang="ar">
                      {a.title.ar}
                    </span>
                    <span className="mt-2 block text-[12.5px] leading-relaxed text-[#cfe0cf]/65">
                      {a.summary[bl]}
                    </span>
                    <span className="mt-3 inline-flex items-center gap-1.5 text-[12px] font-bold text-[#d4af37]">
                      {open ? (
                        <>
                          <ChevronDown className="h-3.5 w-3.5 rotate-180 transition-transform" aria-hidden />
                          {t.common.close}
                        </>
                      ) : (
                        <>
                          <ChevronDown className="h-3.5 w-3.5 transition-transform" aria-hidden />
                          {t.sciences.read}
                        </>
                      )}
                    </span>
                  </span>
                </button>

                {/* ===== CONTENU COMPLET dans la page ===== */}
                {open && (
                  <div className="border-t border-[rgba(212,175,55,0.2)] bg-[rgba(2,25,19,0.45)] px-6 pb-6 pt-4">
                    {a.body[bl].map((par, j) => (
                      <p key={j} className="mb-3 text-[13px] leading-relaxed text-[#e5e0d0]/90">{par}</p>
                    ))}
                    {a.ref && (
                      <figure className="mt-4 rounded-xl border border-[rgba(212,175,55,0.35)] bg-[rgba(2,25,19,0.7)] p-5 text-center">
                        <Quote className="mx-auto mb-2 h-4 w-4 text-[#d4af37]/60" aria-hidden />
                        <p className="font-arabic text-2xl leading-loose text-[#f0d98c]" dir="rtl" lang="ar">{a.ref.ar}</p>
                        <figcaption className="mt-2 text-sm italic text-[#cfe0cf]/85">{a.ref[bl]}</figcaption>
                        <p className="mt-2 text-[11px] font-bold uppercase tracking-widest text-[#d4af37]/70">{a.ref.src}</p>
                      </figure>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
